import {Stack, Typography} from '@mui/material';
import Tutorial from 'presentation/components/Tutorial';
import {translations} from 'presentation/translations-new';
import React from 'react';
import type {Placement, Step} from 'react-joyride';
import {useNavigate} from 'react-router-dom';
import {
	FAVORITE_ADDRESS_TUTORIAL_ACTIONS,
	FAVORITE_ADDRESS_TUTORIAL_STEPS,
	getTutorialStepConfig,
} from './constants';
import {useFavoriteAddressTutorial} from './hook/useFavoriteAddressTutorial';
import type {
	FavoriteAddressTutorialAction,
	FavoriteAddressTutorialStep,
	FavoriteAddressTutorialsProps,
	RenderContentProps,
} from './types';

const {next} = translations['pt-br'].commons;
const {profileMenu, listWithAddresses, addIdentification, addSearch} =
	translations['pt-br'].privateFlow.profile.changeData.addresses.tutorials;

const STEP_CONTENT_BY_STEP: Record<
	FavoriteAddressTutorialStep,
	{
		title: string;
		description: string;
	}
> = {
	[FAVORITE_ADDRESS_TUTORIAL_STEPS.PROFILE_MENU]: profileMenu,
	[FAVORITE_ADDRESS_TUTORIAL_STEPS.LIST_WITH_ADDRESSES]: listWithAddresses,
	[FAVORITE_ADDRESS_TUTORIAL_STEPS.LIST_EMPTY]: listWithAddresses,
	[FAVORITE_ADDRESS_TUTORIAL_STEPS.ADD_IDENTIFICATION]: addIdentification,
	[FAVORITE_ADDRESS_TUTORIAL_STEPS.ADD_SEARCH]: addSearch,
};

export const FavoriteAddressTutorials = ({
	step,
	isBlocked = false,
}: FavoriteAddressTutorialsProps) => {
	const navigate = useNavigate();
	const stepConfig = getTutorialStepConfig(step);
	const {persistStepProgress, isActiveStep, storageKey, targetReady, targetSelector} =
		useFavoriteAddressTutorial(step, isBlocked);
	const nextPath = stepConfig.navigationPath;

	const renderContent = ({title, description}: RenderContentProps) => (
		<Stack>
			<Typography variant="subtitle1" fontWeight={600}>
				{title}
			</Typography>
			<Typography variant="body2">{description}</Typography>
		</Stack>
	);

	const renderSteps = (): Step[] => {
		return [
			{
				target: targetSelector,
				content: renderContent(STEP_CONTENT_BY_STEP[step]),
				placement: stepConfig.placement as Placement,
				disableBeacon: true,
				showSkipButton: Boolean(stepConfig.secondaryCtaLabel),
			},
		];
	};

	const handleStepAction = (action: FavoriteAddressTutorialAction) => {
		persistStepProgress();

		if (action === FAVORITE_ADDRESS_TUTORIAL_ACTIONS.COMPLETE && nextPath) {
			navigate(nextPath);
		}
	};

	if (!isActiveStep || !targetReady) {
		return null;
	}

	return (
		<Tutorial
			run={true}
			steps={renderSteps()}
			storageKey={storageKey}
			persistOnMount={false}
			onComplete={() => handleStepAction(FAVORITE_ADDRESS_TUTORIAL_ACTIONS.COMPLETE)}
			onSecondaryAction={() => handleStepAction(FAVORITE_ADDRESS_TUTORIAL_ACTIONS.SKIP)}
			onClose={() =>
				handleStepAction(
					stepConfig.secondaryCtaLabel
						? FAVORITE_ADDRESS_TUTORIAL_ACTIONS.SKIP
						: FAVORITE_ADDRESS_TUTORIAL_ACTIONS.COMPLETE,
				)
			}
			customButtonsLabels={{
				last: stepConfig.ctaLabel,
				next: next,
				skip: stepConfig.secondaryCtaLabel,
			}}
			customContainerWidth="328px"
			disableScrollParentFix={false}
		/>
	);
};
